import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  CalendarCheck, IdentificationCard, Users, Receipt, Bed, Heartbeat, SignOut, BellRinging,
  CheckCircle, Play, Pause,
} from "@phosphor-icons/react";
import { useAutoPlay } from "../lib/useAutoPlay";
import { ScreenShot, BrowserFrame, MockBilling } from "./ProductMock";
import { DischargeReplica, HospitalSiteReplica } from "./replicas/SiteReplicas";

const STEPS = {
  en: [
    { icon: CalendarCheck, name: "Book", outcome: "Fewer empty slots, no double booking.", bullets: ["Patients book online or by phone; walk-ins fit around them.", "Every booking lands straight in your OPD queue."] },
    { icon: IdentificationCard, name: "Register", outcome: "No duplicate files.", bullets: ["Find any patient by mobile number in seconds.", "One record per patient, with a QR patient card."] },
    { icon: Users, name: "OPD", outcome: "Shorter waits, calmer front desk.", bullets: ["Token queue, vitals, doctor's notes and tests in one flow.", "Everyone sees the same live queue, no shouting across the lobby."] },
    { icon: Receipt, name: "Bill & collect", outcome: "Every rupee recorded the moment it's earned.", bullets: ["GST bill and a UPI QR on the spot, receipt on 80 mm or A4.", "Part payments and dues tracked patient by patient."] },
    { icon: Bed, name: "Admit", outcome: "Know every bed, every rupee, at a glance.", bullets: ["Live bed board across general, private, ICU and day care.", "Advance collected at admission and shown on the running bill."] },
    { icon: Heartbeat, name: "Ward care", outcome: "Nothing missed at 3 am, and every dose reaches the bill.", bullets: ["Nurses scan the bed QR to record vitals and medicines.", "Overdue tasks and early-warning scores alert the team."] },
    { icon: SignOut, name: "Discharge", outcome: "No patient leaves with an unpaid bill.", bullets: ["Doctor signs the summary, cashier clears the dues.", "Two keys, both on record."] },
    { icon: BellRinging, name: "Follow-up", outcome: "More repeat visits.", bullets: ["Reminders bring patients back for review.", "Overdue follow-ups are listed for every doctor."] },
  ],
  hi: [
    { icon: CalendarCheck, name: "बुकिंग", outcome: "कम ख़ाली स्लॉट, कोई डबल बुकिंग नहीं।", bullets: ["मरीज़ ऑनलाइन या फ़ोन से बुक करते हैं; वॉक-इन उनके बीच फ़िट होते हैं।", "हर बुकिंग सीधे आपकी OPD क्यू में आती है।"] },
    { icon: IdentificationCard, name: "रजिस्ट्रेशन", outcome: "कोई डुप्लिकेट फ़ाइल नहीं।", bullets: ["मोबाइल नंबर से किसी भी मरीज़ को सेकंडों में खोजें।", "हर मरीज़ का एक ही रिकॉर्ड, QR पेशेंट कार्ड के साथ।"] },
    { icon: Users, name: "OPD", outcome: "कम इंतज़ार, शांत फ्रंट डेस्क।", bullets: ["टोकन क्यू, वाइटल्स, डॉक्टर के नोट्स और जाँच एक ही प्रवाह में।", "सबको एक ही लाइव क्यू दिखती है।"] },
    { icon: Receipt, name: "बिल व वसूली", outcome: "हर रुपया कमाते ही दर्ज।", bullets: ["मौक़े पर GST बिल और UPI QR, 80 मिमी या A4 पर रसीद।", "आंशिक भुगतान और बक़ाया मरीज़-वार दर्ज।"] },
    { icon: Bed, name: "एडमिशन", outcome: "हर बेड, हर रुपया एक नज़र में।", bullets: ["जनरल, प्राइवेट, ICU और डे केयर का लाइव बेड बोर्ड।", "एडमिशन पर एडवांस, रनिंग बिल में दर्ज।"] },
    { icon: Heartbeat, name: "वार्ड केयर", outcome: "रात 3 बजे भी कुछ नहीं छूटता, और हर डोज़ बिल तक पहुँचती है।", bullets: ["नर्स बेड का QR स्कैन कर वाइटल्स और दवा दर्ज करती हैं।", "बकाया टास्क और अर्ली-वॉर्निंग स्कोर टीम को सचेत करते हैं।"] },
    { icon: SignOut, name: "डिस्चार्ज", outcome: "कोई मरीज़ बिना बिल चुकाए नहीं जाता।", bullets: ["डॉक्टर सारांश साइन करते हैं, कैशियर बक़ाया क्लियर करता है।", "दो की, दोनों रिकॉर्ड में।"] },
    { icon: BellRinging, name: "फ़ॉलो-अप", outcome: "ज़्यादा दोबारा विज़िट।", bullets: ["रिमाइंडर मरीज़ों को वापस लाते हैं।", "हर डॉक्टर के लिए बकाया फ़ॉलो-अप सूचीबद्ध।"] },
  ],
};

const SCREENS = [
  { src: "/screens/reception-home.webp", alt: "Atikxzio HMS receptionist home with online bookings and today's queue" },
  { src: "/screens/reception-home.webp", alt: "Patient search and registration in Atikxzio HMS" },
  { src: "/screens/doctor-home.webp", alt: "Doctor's home in Atikxzio HMS with the OPD queue and follow-ups" },
  null, // billing replica
  { src: "/screens/bed-board.webp", alt: "Live bed board across day care, general and private wards in Atikxzio HMS" },
  { src: "/screens/vitals-mar.webp", alt: "Patient vitals trend and medication administration record in Atikxzio HMS" },
  null, // discharge replica
  { src: "/screens/doctor-home.webp", alt: "Follow-ups due list in Atikxzio HMS" },
];

const COPY = {
  en: {
    eyebrow: "How Atikxzio works",
    title: "The patient journey, start to finish",
    sub: "From the first phone call to the follow-up reminder — one record, one bill, one system.",
    step: "Step",
    hint: "Auto-playing · tap any step",
    play: "Play the journey", pause: "Pause the journey",
  },
  hi: {
    eyebrow: "Atikxzio कैसे काम करता है",
    title: "मरीज़ की पूरी यात्रा, शुरू से अंत तक",
    sub: "पहले फ़ोन कॉल से फ़ॉलो-अप रिमाइंडर तक — एक रिकॉर्ड, एक बिल, एक सिस्टम।",
    step: "चरण",
    hint: "अपने आप चल रहा है · किसी भी चरण पर टैप करें",
    play: "यात्रा चलाएँ", pause: "रोकें",
  },
};

const INTERVAL = 3500;
const RESUME_AFTER = 8000;

const StepScreen = ({ index, step, lang }) => {
  const screen = SCREENS[index];
  return screen ? (
    <div className="rounded-[20px] bg-navy p-3 shadow-[0_18px_48px_rgba(11,30,77,0.18)]">
      <ScreenShot src={screen.src} alt={screen.alt} label={`atikxzio.com/${step.name.toLowerCase()}`} lang={lang} caption={false} />
    </div>
  ) : (
    <div className="rounded-[20px] bg-navy p-3 shadow-[0_18px_48px_rgba(11,30,77,0.18)]">
      <BrowserFrame label="atikxzio.com/billing">
        {index === 3 ? <MockBilling lang={lang} /> : <DischargeReplica lang={lang} />}
      </BrowserFrame>
    </div>
  );
};

export const Journey = ({ lang = "en" }) => {
  const steps = STEPS[lang];
  const c = COPY[lang];
  const reduced = useReducedMotion();

  const { ref, active, playing, pick, toggle, mounted } = useAutoPlay(steps.length, { first: 3000, every: INTERVAL, hold: RESUME_AFTER });

  /* Reduced motion: a plain, complete list. */
  if (reduced) {
    return (
      <div data-testid="journey">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-electric-ink">{c.eyebrow}</p>
        <h2 className="mt-4 max-w-3xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-navy lg:text-[2.5rem]">{c.title}</h2>
        <p className="mt-4 max-w-2xl text-[17px] text-ink">{c.sub}</p>
        <ol className="mt-10 space-y-5">
          {steps.map((st, i) => (
            <li key={st.name} className="flex items-start gap-4 rounded-[20px] border border-navy/10 bg-white p-5" data-testid={`journey-step-${i}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-white"><st.icon size={19} weight="duotone" /></span>
              <span>
                <span className="block font-display text-sm font-bold text-navy">{c.step} {i + 1} · {st.name}</span>
                <span className="mt-1 block text-sm text-teal-ink">{st.outcome}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  const step = steps[active];
  const progress = (active / (steps.length - 1)) * 100;

  return (
    <div ref={ref} data-testid="journey">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-electric-ink">{c.eyebrow}</p>
          <h2 className="mt-4 max-w-3xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-navy lg:text-[2.5rem]">{c.title}</h2>
          <p className="mt-4 max-w-2xl text-[17px] text-ink">{c.sub}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            aria-label={playing ? c.pause : c.play}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/15 bg-white text-navy transition-colors duration-200 hover:border-navy/40"
            data-testid="journey-playpause"
          >
            {playing ? <Pause size={15} weight="fill" /> : <Play size={15} weight="fill" />}
          </button>
        </div>
      </div>

      {/* Desktop: step rail with a filling progress line */}
      <div className="relative mt-12 hidden lg:block">
        <span aria-hidden className="absolute left-0 top-5 h-px w-full bg-navy/12" />
        {mounted ? (
          <motion.span
            aria-hidden
            className="absolute left-0 top-5 h-[2px] bg-teal"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        ) : (
          <span aria-hidden className="absolute left-0 top-5 h-[2px] bg-teal" style={{ width: "0%" }} />
        )}
        <ol className="relative flex gap-2">
          {steps.map((st, i) => (
            <li key={st.name} className="flex-1">
              <button onClick={() => pick(i)} aria-pressed={active === i} className="group block w-full text-left" data-testid={`journey-step-${i}`}>
                <span className="relative block">
                  {mounted && active === i && (
                    <motion.span
                      aria-hidden
                      className="absolute left-0 top-0 h-10 w-10 rounded-full bg-teal/30"
                      animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                    />
                  )}
                  <span className={`relative flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
                    active === i ? "border-teal bg-teal text-white" : i < active ? "border-teal/50 bg-white text-teal-ink" : "border-navy/15 bg-white text-navy/50 group-hover:border-teal/50"
                  }`}>
                    <st.icon size={19} weight="duotone" />
                  </span>
                </span>
                <span className="mt-4 block pr-3">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{c.step} {i + 1}</span>
                  <span className={`block font-display text-sm font-bold ${active === i ? "text-navy" : "text-navy/70"}`}>{st.name}</span>
                  <span className="mt-1 block text-xs leading-snug text-teal-ink">{st.outcome}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      {/* Desktop detail */}
      <div className="mt-12 hidden items-start gap-10 lg:grid lg:grid-cols-12">
        <div className="lg:col-span-5">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step.name}
              initial={mounted ? { opacity: 0, y: 12 } : false}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              data-testid={`journey-detail-${active}`}
            >
              <h3 className="font-display text-xl font-bold text-navy">{step.name}</h3>
              <p className="mt-2 text-sm font-semibold text-teal-ink">{step.outcome}</p>
              <ul className="mt-5 space-y-3">
                {step.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-ink">
                    <CheckCircle size={17} weight="fill" className="mt-0.5 shrink-0 text-teal-ink" /> {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`screen-${active}`}
              initial={mounted ? { opacity: 0 } : false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
            >
              <StepScreen index={active} step={step} lang={lang} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Phone: one card at a time, swipeable */}
      <div className="mt-10 lg:hidden">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500" data-testid="journey-counter">{active + 1} / {steps.length}</span>
          <div className="flex gap-1.5">
            {steps.map((st, i) => (
              <button key={st.name} onClick={() => pick(i)} aria-label={`${c.step} ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ${active === i ? "w-6 bg-teal" : "w-1.5 bg-navy/20"}`} data-testid={`journey-dot-${i}`} />
            ))}
          </div>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`m-${step.name}`}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, info) => {
              if (info.offset.x < -60) pick((active + 1) % steps.length);
              else if (info.offset.x > 60) pick((active - 1 + steps.length) % steps.length);
            }}
            initial={mounted ? { opacity: 0, y: 14 } : false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="mt-4 rounded-[20px] border border-navy/10 bg-white p-5"
            data-testid={`journey-card-${active}`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-teal text-white"><step.icon size={19} weight="duotone" /></span>
            <h3 className="mt-4 font-display text-lg font-bold text-navy">{step.name}</h3>
            <p className="mt-1.5 text-sm font-semibold text-teal-ink">{step.outcome}</p>
            <ul className="mt-4 space-y-2.5">
              {step.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm leading-relaxed text-ink">
                  <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-teal-ink" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-5"><StepScreen index={active} step={step} lang={lang} /></div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export const SiteSpotlight = ({ lang = "en" }) => <HospitalSiteReplica lang={lang} />;
